/**
 * ProtectedRoute — enforces three access gates in strict order.
 * supabase-authSKILL Rule 4.
 *
 * Gate 1 — Authentication:   user must be signed in
 * Gate 2 — COPPA Consent:    managed child (is_managed) must have consent_given = true
 * Gate 3 — Subscription:     active trial or paid subscription required
 *                             (skipped when requireSubscription = false)
 *
 * Recovery UI — shown when profile fetch times out or errors (D-029).
 * Never redirects to login on timeout — that would cause a login loop for
 * users with valid sessions whose profile DB query was temporarily slow.
 */
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth }               from '@/context/AuthContext'
import { LoadingScreen }         from '@/components/LoadingScreen'
import { clearAndRedirect }      from '@/lib/authCleanup'
import { ROUTES }                from '@/lib/routes'

interface Props {
  children:             React.ReactNode
  requireSubscription?: boolean                    // default true — set false for billing/settings pages
  requireRole?:         'parent' | 'student'       // when set, redirects wrong-role users to /home
}

export function ProtectedRoute({ children, requireSubscription = true, requireRole }: Props) {
  const {
    user, profile, loading, profileLoading, profileFetchFailed, retryProfileLoad,
  } = useAuth()
  const location = useLocation()

  // Gate 0 — Auth bootstrap: wait for onAuthStateChange INITIAL_SESSION (D-025)
  // AuthContext's 20 s watchdog fails soft first (unblocks UI, preserves
  // session). This 22 s UI-layer safety net is the last resort if even that
  // never fires — it shows the recovery overlay (manual reload / sign-out),
  // never an automatic wipe (June 12, 2026).
  if (loading) {
    console.log('[Nudge/auth] ProtectedRoute: waiting for auth bootstrap')
    return <LoadingScreen timeoutMs={22_000} clearOnTimeout />
  }

  // Gate 1 — Authentication
  if (!user) {
    console.log('[Nudge/auth] ProtectedRoute: no user — redirect to login')
    const isAP = location.pathname.startsWith('/ap')
    // AP routes → AP login (branded, supports Google Sign-In)
    // K-12 routes → shared login
    const loginUrl = isAP
      ? `${ROUTES.AP_LOGIN}?returnTo=${encodeURIComponent(location.pathname)}`
      : ROUTES.LOGIN
    return <Navigate to={loginUrl} state={{ from: location }} replace />
  }

  // Gate 1b — INITIAL profile fetch in-flight (D-028/D-029)
  // 27 s > 25 s (PROFILE_FETCH_TIMEOUT_MS 15 s + retry 10 s), so the recovery
  // UI never fires while the auto-retry is still in flight. INITIAL_SESSION's
  // loadProfile succeeds within that window on the first cold-start.
  //
  // `&& !profile` (June 12, 2026): a REFETCH (refreshProfile mid-session —
  // Onboarding handleFinish, SubscriptionSuccess polling, Paywall) must keep
  // the current page mounted with the stale profile (stale-while-revalidate).
  // The old unconditional swap unmounted the page and destroyed its local
  // state — parents finishing onboarding bounced back to "Step 1 Welcome"
  // and never saw the done screen with the child's login link.
  if (profileLoading && !profile) {
    console.log('[Nudge/auth] ProtectedRoute: profile loading…')
    return <LoadingScreen timeoutMs={27_000} />
  }

  // Gate 1c — INITIAL profile fetch failed (timeout or DB error).
  // Show recovery UI — do NOT redirect to login (that causes a redirect loop
  // because the user has a valid session; the problem is DB latency, not auth).
  // `&& !profile`: a failed mid-session REFETCH keeps the page mounted on the
  // stale profile — same stale-while-revalidate rule as Gate 1b.
  if (profileFetchFailed && !profile) {
    console.warn('[Nudge/auth] ProtectedRoute: profile fetch failed — showing recovery UI')
    return (
      <div className="min-h-screen bg-page flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <div className="text-4xl mb-4" aria-hidden="true">⚠️</div>
          <h2 className="font-heading text-xl font-bold text-text-primary mb-2">
            Couldn't load your profile
          </h2>
          <p className="text-text-muted text-sm mb-6">
            This usually happens after inactivity or a slow connection.
            Your session is still valid — just tap <strong>Try again</strong>.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => { void retryProfileLoad() }}
              className="btn-primary w-full"
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="btn-ghost w-full"
            >
              Reload app
            </button>
            <button
              onClick={() => clearAndRedirect('profile-fetch-signout')}
              className="text-sm text-text-muted hover:text-text-primary underline"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Gate 1d — Profile fetch completed but returned nothing.
  // This means the user exists in auth but has no profile row — a genuine
  // data inconsistency, not a latency issue. Redirect to login.
  if (!profile) {
    console.warn('[Nudge/auth] ProtectedRoute: user has no profile row — redirect to login')
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />
  }

  // Gate 2 — COPPA Consent (managed child accounts ONLY)
  //
  // COPPA's verifiable-parental-consent obligation applies to parent-created
  // child accounts — is_managed = true, set by create-child-account. Self-signup
  // students passed the hard 13+ age gate (Signup.tsx, CLAUDE.md §6.6) and AP
  // students (16–18) are self-managed (§6.1) — neither is COPPA-covered, and
  // their parent_id is NULL so parental consent could never arrive.
  //
  // June 12, 2026 incident: this gate previously matched ALL students
  // (role === 'student' && !consent_given). Google OAuth cannot carry role
  // metadata, so handle_new_user defaults every OAuth signup to role='student',
  // consent_given=false → the gate redirected every fresh Google/AP/13+ email
  // signup to /pending-consent, which is itself wrapped in ProtectedRoute →
  // Navigate-to-self loop → permanent blank page. The pathname guard below
  // makes the gate idempotent at its own destination so PendingConsent can
  // actually render for managed children awaiting consent.
  if (
    profile.is_managed &&
    !profile.consent_given &&
    location.pathname !== ROUTES.PENDING_CONSENT
  ) {
    return <Navigate to={ROUTES.PENDING_CONSENT} replace />
  }

  // Gate 2b — Role guard (optional)
  // Prevents students from reaching parent-only pages and vice versa.
  if (requireRole && profile.role !== requireRole) {
    console.warn(`[Nudge/auth] ProtectedRoute: role '${profile.role}' cannot access '${requireRole}'-only route — redirect to home`)
    return <Navigate to={ROUTES.HOME} replace />
  }

  // Gate 3 — Subscription
  if (requireSubscription) {
    const { subscription_status, trial_ends_at } = profile

    const trialExpired =
      subscription_status === 'trial' &&
      trial_ends_at != null &&
      new Date(trial_ends_at) < new Date()

    const blocked =
      subscription_status === 'canceled' ||
      subscription_status === 'past_due'  ||
      trialExpired

    if (blocked) {
      return <Navigate to="/paywall" replace />
    }
  }

  return <>{children}</>
}
